import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, IndianRupee, DollarSign, CheckCircle2, AlertCircle, ExternalLink, Copy } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const Rail = ({ name, icon: Icon, data, accent }) => {
  const copyWebhook = async () => {
    const url = `${window.location.origin}${data.webhook_url}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Webhook URL copied');
    } catch {
      toast.message('Webhook URL', { description: url });
    }
  };

  const modeConfig = {
    live:         { label: 'LIVE',         color: '#39FF14', bg: 'rgba(57,255,20,0.1)',  border: 'rgba(57,255,20,0.4)' },
    test:         { label: 'TEST MODE',    color: '#FFB17A', bg: 'rgba(255,107,26,0.1)', border: 'rgba(255,107,26,0.4)' },
    unconfigured: { label: 'KEYS NEEDED',  color: '#FF6B6B', bg: 'rgba(255,107,107,0.1)', border: 'rgba(255,107,107,0.4)' },
  };
  const m = modeConfig[data.mode] || modeConfig.unconfigured;

  return (
    <div className="relative overflow-hidden rounded-xl border border-white/5 bg-black/40 p-5 flex-1 min-w-0" data-testid={`admin-rail-${name.toLowerCase()}`}>
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg grid place-items-center bg-white/[0.03] border border-white/10" style={{ boxShadow: `inset 0 0 12px ${accent}22` }}>
            <Icon className="h-4 w-4" style={{ color: accent }} />
          </div>
          <div>
            <div className="display text-base font-semibold">{name}</div>
            <div className="text-[10px] uppercase tracking-widest mono text-white/45">
              {data.currencies?.join(' · ')} · {data.regions?.join(' / ')}
            </div>
          </div>
        </div>
        <span
          className="text-[9px] uppercase tracking-widest mono px-2 py-1 rounded-full border inline-flex items-center gap-1"
          style={{ color: m.color, borderColor: m.border, background: m.bg }}
        >
          {data.mode === 'live' || data.mode === 'test'
            ? <CheckCircle2 className="h-2.5 w-2.5" />
            : <AlertCircle className="h-2.5 w-2.5" />}
          {m.label}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-[11px]">
        <div>
          <div className="text-white/40 mono uppercase tracking-widest">Transactions</div>
          <div className="display text-lg font-semibold tabular-nums mt-0.5" style={{ color: accent }}>{Number(data.transactions || 0).toLocaleString()}</div>
        </div>
        <div>
          <div className="text-white/40 mono uppercase tracking-widest">Webhook</div>
          <div className="mt-0.5">
            {data.mode === 'unconfigured' ? (
              <span className="text-white/55 text-[11px]">—</span>
            ) : (
              <button onClick={copyWebhook} className="inline-flex items-center gap-1 text-[11px] text-white/75 hover:text-[#39FF14] mono truncate">
                <Copy className="h-3 w-3" /> copy URL
              </button>
            )}
          </div>
        </div>
      </div>

      {data.missing_keys && data.missing_keys.length > 0 && (
        <div className="mt-3 rounded-lg border border-[#FF6B6B]/35 bg-[#FF6B6B]/5 p-2.5 text-[11px] text-white/75">
          <div className="font-medium text-[#FF6B6B] mb-1 mono uppercase tracking-widest">Add to backend/.env</div>
          <ul className="space-y-0.5">
            {data.missing_keys.map((k) => (
              <li key={k} className="mono text-white/70">{k}=…</li>
            ))}
          </ul>
          {name === 'Razorpay' && (
            <a
              href="https://dashboard.razorpay.com/app/keys"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#FFB17A] hover:text-[#39FF14] transition-colors"
            >
              Get keys from Razorpay Dashboard <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export const PaymentRailsCard = () => {
  const { data } = useQuery({
    queryKey: ['admin-payment-rails'],
    queryFn: async () => (await api.get('/admin/payment-rails')).data,
    refetchInterval: 30_000,
  });

  if (!data) return null;

  return (
    <div className="relative overflow-hidden rounded-xl border border-white/5 bg-black/40 backdrop-blur p-5" data-testid="admin-payment-rails-card">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#39FF14] to-transparent" />
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <span className="text-[9px] uppercase tracking-[0.3em] mono text-[#39FF14]">// payment rails</span>
          <h2 className="display text-base font-semibold mt-1 inline-flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-[#39FF14]" /> Checkout rails status
          </h2>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-3">
        <Rail name="Stripe" icon={DollarSign} data={data.stripe} accent="#FF6B1A" />
        <Rail name="Razorpay" icon={IndianRupee} data={data.razorpay} accent="#39FF14" />
      </div>
    </div>
  );
};

export default PaymentRailsCard;
